// Live-preview controls: the size preset picker plus free width/height entry,
// the value stepper (quick presets + free numeric entry), and the stale
// toggle. Presentational only — every value is owned by the caller
// (preview-panel.tsx) and threaded back through these callbacks; this
// component never builds a draft itself.

import { Button, NumberStepper, SegmentedControl, Switch } from "@pej/kore/components";

// Absolute pixels are invisible in the preview — PreviewFrame scales the
// iframe to the panel's width — so what a preset really selects is the shape,
// which is what decides whether the content fits. Both customer screens are
// here: a 4:3 television and the 1920×110 signage strip that the old two
// presets (1.78 and 0.56) made impossible to author against.
export const SIZE_PRESETS = {
    landscape: { label: "16:9", width: 1920, height: 1080 },
    portrait: { label: "9:16", width: 1080, height: 1920 },
    fourThree: { label: "4:3", width: 1024, height: 768 },
    banner: { label: "Banner", width: 1920, height: 110 },
} as const;

export type SizePresetKey = keyof typeof SIZE_PRESETS;

/** Quick values the operator taps to preview a screen at a typical, a busy,
 *  and a wildly-backed-up wait, without hand-typing a number every time. */
export const QUICK_VALUES = [5, 12, 45, 250] as const;

export const DEFAULT_VALUE_MINUTES: number = QUICK_VALUES[1];

export interface PreviewSize {
    width: number;
    height: number;
}

export interface PreviewControlsProps {
    width: number;
    height: number;
    onSizeChange: (size: PreviewSize) => void;
    valueMinutes: number;
    onValueMinutesChange: (value: number) => void;
    stale: boolean;
    onStaleChange: (stale: boolean) => void;
}

export function PreviewControls({
    width,
    height,
    onSizeChange,
    valueMinutes,
    onValueMinutesChange,
    stale,
    onStaleChange,
}: PreviewControlsProps) {
    const presetKeys = Object.keys(SIZE_PRESETS) as SizePresetKey[];
    // A preset is "selected" only when both numbers match it exactly — free
    // width/height entry (or the drag handle) leaves none selected, which is
    // correct: the shape no longer matches any preset.
    const selectedPreset = presetKeys.find(
        (key) => SIZE_PRESETS[key].width === width && SIZE_PRESETS[key].height === height,
    );

    return (
        <div className="flex flex-wrap items-center gap-6">
            <SegmentedControl<SizePresetKey>
                aria-label="Preview size"
                value={selectedPreset}
                onValueChange={(key) =>
                    onSizeChange({ width: SIZE_PRESETS[key].width, height: SIZE_PRESETS[key].height })
                }
                options={presetKeys.map((key) => ({
                    value: key,
                    label: SIZE_PRESETS[key].label,
                }))}
            />
            <div className="flex items-center gap-2">
                <NumberStepper
                    ariaLabel="Preview width"
                    value={width}
                    onChange={(next) => {
                        if (next !== "") onSizeChange({ width: next, height });
                    }}
                    min={1}
                    step={10}
                    unit="px"
                    // kore fixes the value input at w-[3ch] when a unit is shown; widen it
                    // so a 4-digit screen size (1920) isn't clipped.
                    className="[&_input]:w-[5ch]"
                />
                <NumberStepper
                    ariaLabel="Preview height"
                    value={height}
                    onChange={(next) => {
                        if (next !== "") onSizeChange({ width, height: next });
                    }}
                    min={1}
                    step={10}
                    unit="px"
                    className="[&_input]:w-[5ch]"
                />
            </div>
            <div className="flex items-center gap-2">
                {QUICK_VALUES.map((quickValue) => (
                    <Button
                        key={quickValue}
                        type="button"
                        variant={valueMinutes === quickValue ? "default" : "outline"}
                        onClick={() => onValueMinutesChange(quickValue)}
                    >
                        {quickValue}
                    </Button>
                ))}
                <NumberStepper
                    ariaLabel="Minutes to preview"
                    value={valueMinutes}
                    onChange={(next) => {
                        if (next !== "") onValueMinutesChange(next);
                    }}
                    min={0}
                    unit="min"
                />
            </div>
            <label className="flex items-center gap-2 text-sm text-muted-foreground" htmlFor="preview-stale-toggle">
                <Switch id="preview-stale-toggle" checked={stale} onCheckedChange={onStaleChange} />
                Stale data
            </label>
        </div>
    );
}
